import { useState, useCallback } from 'react';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { UserRoleType } from '../../../config/roles';
import { useAssignUserMutation, useCreateOrganizationMutation } from '../adminTools.mutations';
import type { CreateOrganizationResponse } from '../../../types/api';

export interface UseOrgManagementReturn {
  // Org management dialog
  dialogOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  // Expanded org row — drives the parent's per-org users query
  expandedOrgId: string | null;
  toggleOrgExpand: (orgId: string) => void;
  // Assignment state
  selectedOrgForAssignment: { [userId: string]: string };
  selectedRolesForAssignment: { [userId: string]: string[] };
  assigningUserId: string | null;
  unassignedUsersError: string;
  setSelectedOrgForAssignment: (next: { [userId: string]: string }) => void;
  toggleRoleForAssignment: (userId: string, role: string) => void;
  assignUserToOrg: (userId: string) => Promise<void>;
  // Create organization dialog
  createOrgDialogOpen: boolean;
  newOrgName: string;
  createOrgResult: CreateOrganizationResponse | null;
  createOrgError: string;
  creatingOrg: boolean;
  openCreateOrgDialog: () => void;
  closeCreateOrgDialog: () => void;
  setNewOrgName: (value: string) => void;
  createOrganization: () => Promise<void>;
}

export function useOrgManagement(): UseOrgManagementReturn {
  const { showError, showSuccess } = useErrorHandler();
  const assignUserMutation = useAssignUserMutation();
  const createOrganizationMutation = useCreateOrganizationMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedOrgId, setExpandedOrgId] = useState<string | null>(null);
  const [selectedOrgForAssignment, setSelectedOrgForAssignment] = useState<{
    [userId: string]: string;
  }>({});
  const [selectedRolesForAssignment, setSelectedRolesForAssignment] = useState<{
    [userId: string]: string[];
  }>({});
  const [unassignedUsersError, setUnassignedUsersError] = useState('');

  const [createOrgDialogOpen, setCreateOrgDialogOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [createOrgResult, setCreateOrgResult] = useState<CreateOrganizationResponse | null>(null);
  const [createOrgError, setCreateOrgError] = useState('');

  const assigningUserId = assignUserMutation.isPending
    ? (assignUserMutation.variables as { userId: string } | undefined)?.userId ?? null
    : null;

  const openDialog = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setExpandedOrgId(null);
    setSelectedOrgForAssignment({});
    setSelectedRolesForAssignment({});
  }, []);

  const toggleOrgExpand = useCallback((orgId: string) => {
    setExpandedOrgId((prev) => (prev === orgId ? null : orgId));
  }, []);

  const toggleRoleForAssignment = useCallback((userId: string, role: string) => {
    setSelectedRolesForAssignment((prev) => {
      const currentRoles = prev[userId] || [];
      const newRoles = currentRoles.includes(role)
        ? currentRoles.filter((r) => r !== role)
        : [...currentRoles, role];
      return { ...prev, [userId]: newRoles };
    });
  }, []);

  const assignUserToOrg = useCallback(
    async (userId: string) => {
      const orgId = selectedOrgForAssignment[userId];
      const roles = selectedRolesForAssignment[userId] || [];

      if (!orgId || roles.length === 0) {
        setUnassignedUsersError('Please select an organization and at least one role');
        return;
      }

      try {
        await assignUserMutation.mutateAsync({ userId, orgId, roles: roles as UserRoleType[] });

        setSelectedOrgForAssignment((prev) => {
          const newState = { ...prev };
          delete newState[userId];
          return newState;
        });
        setSelectedRolesForAssignment((prev) => {
          const newState = { ...prev };
          delete newState[userId];
          return newState;
        });
        setUnassignedUsersError('');
        showSuccess('User assigned to organization successfully');
      } catch (error) {
        showError(error, 'Failed to assign user to organization');
      }
    },
    [
      selectedOrgForAssignment,
      selectedRolesForAssignment,
      assignUserMutation,
      showError,
      showSuccess,
    ]
  );

  const openCreateOrgDialog = useCallback(() => {
    setCreateOrgDialogOpen(true);
    setNewOrgName('');
    setCreateOrgResult(null);
    setCreateOrgError('');
  }, []);

  const closeCreateOrgDialog = useCallback(() => {
    setCreateOrgDialogOpen(false);
    setNewOrgName('');
    setCreateOrgResult(null);
    setCreateOrgError('');
  }, []);

  const createOrganization = useCallback(async () => {
    if (!newOrgName.trim()) {
      setCreateOrgError('Organization name is required');
      return;
    }

    setCreateOrgResult(null);
    setCreateOrgError('');

    try {
      const result = await createOrganizationMutation.mutateAsync(newOrgName.trim());
      setCreateOrgResult(result);

      if (result.success) {
        showSuccess('Organization created successfully');
        setNewOrgName('');
      }
    } catch (error) {
      showError(error, 'Failed to create organization');
    }
  }, [newOrgName, createOrganizationMutation, showError, showSuccess]);

  return {
    dialogOpen,
    openDialog,
    closeDialog,
    expandedOrgId,
    toggleOrgExpand,
    selectedOrgForAssignment,
    selectedRolesForAssignment,
    assigningUserId,
    unassignedUsersError,
    setSelectedOrgForAssignment,
    toggleRoleForAssignment,
    assignUserToOrg,
    createOrgDialogOpen,
    newOrgName,
    createOrgResult,
    createOrgError,
    creatingOrg: createOrganizationMutation.isPending,
    openCreateOrgDialog,
    closeCreateOrgDialog,
    setNewOrgName,
    createOrganization,
  };
}
